import { faClock } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, InputAdornment, Paper, styled, ToggleButton, ToggleButtonGroup } from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import dayjs from "dayjs";
import React, { useState } from "react";

export type AMPM = "am" | "pm";

export type TimeFieldProps = TextFieldProps & {
  showTimeTypePicker?: boolean;
  showNowButton?: boolean;
  onTimeChange: (time: string, ampm: AMPM) => void;
  ampm?: AMPM;
};

const TWENTY_FOUR_HOUR_REGEX = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const TWELVE_HOUR_REGEX = /^(1[0-2]|0?[1-9]):([0-5]\d)(?::([0-5]\d))?\s*([aApP][mM])$/;

function normalizeTimeValue(text?: string): string {
  const trimmed = (text ?? "").trim();
  if (trimmed.length === 0) {
    return "";
  }

  // Handle numeric-only input like "615" → "6:15" or "945" → "9:45"
  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length === 1 || trimmed.length === 2) {
      // Single or double digit: treat as hours only (e.g., "6" → "6:00" or "12" → "12:00")
      const hour = Number(trimmed);
      if (hour >= 0 && hour <= 23) {
        return `${hour % 12 || 12}:00`;
      }
      return "";
    } else if (trimmed.length === 3) {
      // Three digits: first digit is hour, last two are minutes (e.g., "615" → "6:15")
      const hour = Number(trimmed[0]);
      const minute = Number(trimmed.slice(1));
      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return `${hour % 12 || 12}:${minute.toString().padStart(2, "0")}`;
      }
      return "";
    } else if (trimmed.length === 4) {
      // Four digits: first two are hour, last two are minutes (e.g., "0945" → "9:45")
      const hour = Number(trimmed.slice(0, 2));
      const minute = Number(trimmed.slice(2));
      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return `${hour % 12 || 12}:${minute.toString().padStart(2, "0")}`;
      }
      return "";
    }
    return "";
  }

  const twentyFourHourMatch = trimmed.match(TWENTY_FOUR_HOUR_REGEX);
  if (twentyFourHourMatch) {
    const hour24 = Number(twentyFourHourMatch[1]);
    const minute = twentyFourHourMatch[2];
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${minute}`;
  }

  const twelveHourMatch = trimmed.match(TWELVE_HOUR_REGEX);
  if (twelveHourMatch) {
    const hour12 = Number(twelveHourMatch[1]);
    const minute = twelveHourMatch[2];
    return `${hour12}:${minute}`;
  }

  return "";
}

export function TimeField(props: TimeFieldProps) {
  const { ampm } = props;
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const normalizedPropValue = normalizeTimeValue(props.value as string);
  const value = draftValue ?? normalizedPropValue;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftValue(event.target.value);
    props.onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const normalized = normalizeTimeValue(value);
    setDraftValue(null);
    props.onTimeChange(normalized, ampm ?? "am");
    props.onBlur?.(event);
  };

  const handleSetCurrentTime = () => {
    const newValue = dayjs().format("h:mm");
    setDraftValue(null);
    props.onTimeChange(newValue, ampm ?? "am");
  };

  const handleSetAmpm = (ampm: AMPM) => {
    props.onTimeChange(value, ampm ?? "am");
  };

  const { showNowButton, showTimeTypePicker, ...textFieldProps } = props;

  return (
    <>
      <TextField
        hiddenLabel
        size="small"
        sx={{ width: 95 }}
        placeholder="h:mm"
        slotProps={{
          input: {
            inputProps: {
              inputMode: "numeric",
            },
            endAdornment: showNowButton && (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={handleSetCurrentTime}>
                  <FontAwesomeIcon icon={faClock} size="xs" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {showTimeTypePicker && <TimeTypeField onChange={handleSetAmpm} />}
    </>
  );
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: "1px solid transparent",
  },
}));

export function TimeTypeField(props: { onChange: (value: AMPM) => void }) {
  const { onChange } = props;
  const [ampm, setAmpm] = React.useState("am");

  const handleAmpm = (event: React.MouseEvent<HTMLElement>, ampm: AMPM) => {
    setAmpm(ampm);
    onChange(ampm);
  };

  return (
    <div>
      <Paper
        elevation={0}
        sx={(theme) => ({
          display: "flex",
          border: `1px solid ${theme.palette.divider}`,
          flexWrap: "wrap",
        })}
      >
        <StyledToggleButtonGroup size="small" value={ampm} exclusive onChange={handleAmpm} aria-label="AM/PM">
          <ToggleButton value="am" aria-label="left aligned">
            AM
          </ToggleButton>
          <ToggleButton value="pm" aria-label="centered">
            PM
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Paper>
    </div>
  );
}
